import {
  assignPrototypeAPIs,
  assignTableAPIs,
  makeStateUpdater,
  type OnChangeFn,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type TableFeatures,
} from "@tanstack/react-table";

interface RangeSelectTableState {
  lastSelectedRowId: string | null;
}

export interface RangeSelectOptions {
  onLastSelectedRowIdChange?: OnChangeFn<string | null>;
}

export interface RangeSelectInstance {
  setLastSelectedRowId: (rowId: string | null) => void;
}

export interface RangeSelectRow {
  toggleSelectedWithRange: (useRange: boolean) => void;
}

declare module "@tanstack/table-core" {
  interface Plugins {
    rangeSelectFeature: TableFeature;
  }
  interface TableState_FeatureMap {
    rangeSelectFeature: RangeSelectTableState;
  }
  interface TableOptions_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    rangeSelectFeature: RangeSelectOptions;
  }
  interface Table_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    rangeSelectFeature: RangeSelectInstance;
  }
  interface Row_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    rangeSelectFeature: RangeSelectRow;
  }
}

interface RangeSelectFeatures {
  rowSelectionFeature: TableFeature;
  rangeSelectFeature: TableFeature;
}
type RangeSelectTable = Table<RangeSelectFeatures, RowData>;
type RangeSelectRowInstance = Row<RangeSelectFeatures, RowData>;

export const rangeSelectFeature: TableFeature = {
  getInitialState: (state) => ({
    lastSelectedRowId: null,
    ...state,
  }),

  getDefaultTableOptions: (table) => ({
    onLastSelectedRowIdChange: makeStateUpdater("lastSelectedRowId", table),
  }),

  constructTableAPIs: (table) => {
    const t = table as unknown as RangeSelectTable;

    assignTableAPIs("rangeSelectFeature", table, {
      table_setLastSelectedRowId: {
        fn: (rowId: string | null) =>
          t.options.onLastSelectedRowIdChange?.(rowId),
      },
    });
  },

  assignRowPrototype: (prototype, table) => {
    assignPrototypeAPIs("rangeSelectFeature", prototype, table, {
      row_toggleSelectedWithRange: {
        fn: (row, useRange: boolean) => {
          const r = row as unknown as RangeSelectRowInstance;
          const { rows } = r.table.getRowModel();
          const anchorIndex = rows.findIndex(
            (tableRow) => tableRow.id === r.table.atoms.lastSelectedRowId.get(),
          );
          const targetIndex = rows.findIndex(
            (tableRow) => tableRow.id === r.id,
          );

          const selected = !r.getIsSelected();

          if (!useRange || anchorIndex === -1 || targetIndex === -1) {
            r.toggleSelected(selected);
          } else {
            const [start, end] =
              anchorIndex < targetIndex
                ? [anchorIndex, targetIndex]
                : [targetIndex, anchorIndex];

            const rangeRows = rows
              .slice(start, end + 1)
              .filter((tableRow) => tableRow.getCanSelect());

            r.table.setRowSelection((currentSelection) => {
              const nextSelection = { ...currentSelection };

              rangeRows.forEach((tableRow) => {
                if (selected) {
                  nextSelection[tableRow.id] = true;
                } else {
                  delete nextSelection[tableRow.id];
                }
              });

              return nextSelection;
            });
          }

          r.table.setLastSelectedRowId(r.id);
        },
      },
    });
  },
};
